// @migration-in-progress
/**
 * Conditional Pre-Departure Checks Database
 * Based on vessel type, size, and operational requirements
 */

// CONDITIONAL CHECKS - Based on vessel type, size, area, etc.
export const CONDITIONAL_CHECKS = {
  // PASSENGER VESSELS
  passenger: {
    trigger: { type: 'type', condition: 'passenger' },
    checks: [
      {
        id: 'safety_briefing_area',
        label: 'Passenger safety briefing areas ready',
        regulation: 'Reg 9',
        type: 'check',
        required: true,
        category: 'safety'
      },
      {
        id: 'muster_lists',
        label: 'Muster lists - verify posted',
        regulation: 'SI 1999/2722',
        type: 'check',
        required: true,
        category: 'safety'
      },
      {
        id: 'pa_system_test',
        label: 'PA system - test operation',
        regulation: 'WBC3 Section 10',
        type: 'test',
        required: true,
        category: 'communications'
      }
    ]
  },
  
  passenger_over_12: {
    trigger: { type: 'personsCapacity', condition: '>12' },
    checks: [
      {
        id: 'passenger_manifest',
        label: 'Passenger manifest - complete',
        regulation: 'WBC3 Section 24 / MSN 1823 Section 5.1',
        type: 'document',
        required: true,
        category: 'documentation',
        recordValue: true,
        valueType: 'number',
        valuePlaceholder: 'Number of passengers'
      },
      {
        id: 'safety_announcement',
        label: 'Safety announcement - complete',
        regulation: 'MSN 1823',
        type: 'procedure',
        required: true,
        category: 'safety'
      },
      {
        id: 'general_alarm_test',
        label: 'General alarm - test (7 short + 1 long)',
        regulation: 'MSN 1823 Section 10.2',
        type: 'test',
        required: true,
        category: 'safety'
      }
    ]
  },

  passenger_over_60: {
    trigger: { type: 'personsCapacity', condition: '>60' },
    checks: [
      {
        id: 'evacuation_system',
        label: 'Evacuation system - verify ready',
        regulation: 'WBC3 Section 14',
        type: 'check',
        required: true,
        category: 'safety'
      }
    ]
  },

  // PILOT BOATS
  pilot: {
    trigger: { type: 'type', condition: 'pilot' },
    checks: [
      {
        id: 'retrieval_equipment',
        label: 'Retrieval equipment - verify ready',
        regulation: 'WBC3 Section 27.2',
        type: 'check',
        required: true,
        category: 'operations'
      },
      {
        id: 'searchlight',
        label: 'Searchlight - test operation',
        regulation: 'WBC3 Section 27.2', // Pilot boat specific requirement
        type: 'test',
        required: true,
        category: 'equipment'
      }
      // Note: Immersion suits covered under general safety equipment check
      // Required for night ops (Cat 3 standards) but not daylight only (Cat 6)
      // Per WBC3 Section 27.2.3
    ]
  },

  // TUGS
  tug: {
    trigger: { type: 'type', condition: 'tug' },
    checks: [
      {
        id: 'towing_equipment',
        label: 'Towing equipment - visual inspection',
        regulation: 'WBC3 Section 26 / MSN 1853',
        type: 'check',
        required: true,
        category: 'operations'
      },
      {
        id: 'emergency_release',
        label: 'Emergency release - verify accessible',
        regulation: 'WBC3 Section 26',
        type: 'check',
        required: true,
        category: 'safety'
      }
    ]
  },

  // DIVE SUPPORT
  dive_support: {
    trigger: { type: 'operations', condition: 'diving' },
    checks: [
      {
        id: 'diving_ladder',
        label: 'Diving ladder - check before use',
        regulation: 'WBC3 Section 25.5',
        type: 'check',
        required: true,
        category: 'operations'
      },
      {
        id: 'emergency_gas',
        label: 'Emergency gas - check pressure',
        regulation: 'WBC3 Section 25.5',
        type: 'check',
        required: true,
        category: 'safety',
        recordValue: true,
        valueType: 'text',
        valuePlaceholder: 'Bar/PSI'
      }
    ]
  },

  // HIGH SPEED (>17 knots)
  high_speed: {
    trigger: { type: 'speed', condition: '>17' },
    checks: [
      {
        id: 'kill_cord',
        label: 'Kill cord - test operation',
        regulation: 'WBC3 Section 25.4',
        type: 'test',
        required: true,
        category: 'safety'
      }
    ]
  },

  // AREA CATEGORY 0-2
  area_0_2: {
    trigger: { type: 'areaCategory', condition: '0-2' },
    checks: [
      {
        id: 'epirb_test',
        label: 'EPIRB - test operation',
        regulation: 'WBC3 Section 14.7',
        type: 'test',
        required: true,
        category: 'communications'
      },
      {
        id: 'sart_test',
        label: 'SART - test if fitted',
        regulation: 'WBC3 Section 14.6',
        type: 'test',
        required: false,
        category: 'communications'
      }
    ]
  },

  // AREA CATEGORY 0-4
  area_0_4: {
    trigger: { type: 'areaCategory', condition: '0-4' },
    checks: [
      {
        id: 'weather_forecast',
        label: 'Weather forecast - obtain and assess',
        regulation: 'WBC3 Section 19',
        type: 'procedure',
        required: true,
        category: 'operations',
        recordValue: true,
        valueType: 'multifield',
        fields: [
          { id: 'wind', label: 'Wind', placeholder: 'Speed/Direction' },
          { id: 'sea_state', label: 'Sea State', placeholder: '0-9' },
          { id: 'visibility', label: 'Visibility', placeholder: 'nm/m' }
        ]
      }
    ]
  },

  // NIGHT OPERATIONS
  night_operations: {
    trigger: { type: 'timeOfDay', condition: 'night' },
    checks: [
      {
        id: 'night_nav_equipment',
        label: 'Night navigation equipment - test',
        regulation: 'WBC3 Section 19 / MGN 469 Section 5.3',
        type: 'test',
        required: true,
        category: 'navigation'
      }
    ]
  },

  // LARGE VESSELS
  large_vessel_24m: {
    trigger: { type: 'length', condition: '>=24' },
    checks: [
      {
        id: 'bridge_equipment',
        label: 'Bridge equipment - test all',
        regulation: 'WBC3 Section 19',
        type: 'test',
        required: true,
        category: 'navigation'
      }
    ]
  },

  large_vessel_300gt: {
    trigger: { type: 'grossTonnage', condition: '>=300' },
    checks: [
      {
        id: 'gmdss_dsc',
        label: 'GMDSS radio - test DSC',
        regulation: 'SOLAS Ch IV',
        type: 'test',
        required: true,
        category: 'communications'
      }
    ]
  },

  // INTERNATIONAL VOYAGES
  international: {
    trigger: { type: 'voyageType', condition: 'international' },
    checks: [
      {
        id: 'isps_compliance',
        label: 'ISPS compliance - verify',
        regulation: 'ISPS Code',
        type: 'document',
        required: true,
        category: 'security'
      },
      {
        id: 'port_clearance',
        label: 'Port clearance - verify obtained',
        regulation: 'Port State Control',
        type: 'document',
        required: true,
        category: 'documentation'
      }
    ]
  },

  // CARGO OPERATIONS
  cargo: {
    trigger: { type: 'operations', condition: 'cargo' },
    checks: [
      {
        id: 'cargo_securing',
        label: 'Cargo securing - verify',
        regulation: 'CSS Code',
        type: 'check',
        required: true,
        category: 'operations'
      }
    ]
  },

  // SAFETY BOAT
  safety_boat: {
    trigger: { type: 'type', condition: 'safety' },
    checks: [
      {
        id: 'mob_recovery_equipment',
        label: 'MOB recovery equipment - check ready',
        regulation: 'WBC3 Section 14',
        type: 'check',
        required: true,
        category: 'safety'
      }
    ]
  },

  // CREW TRANSFER VESSEL
  ctv: {
    trigger: { type: 'type', condition: 'ctv' },
    checks: [
      {
        id: 'transfer_system',
        label: 'Transfer system - check operational',
        regulation: 'WBC3 Section 25',
        type: 'check',
        required: true,
        category: 'operations'
      }
    ]
  },

  // RIB (Rigid Inflatable Boat)
  rib: {
    trigger: { type: 'type', condition: 'rib' },
    checks: [
      {
        id: 'tube_pressure',
        label: 'Tube pressure - check',
        regulation: 'Manufacturer specifications',
        type: 'check',
        required: true,
        category: 'equipment'
      }
    ]
  }
};